import { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import styles from './PersonRecordPanel.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson, ApiError } from '../../lib/api'
import { dmy, money, plural } from '../wages/format'
import { todayLocal } from '../timesheets/time'
import { PersonAvatar, PHOTO_MAX, shrinkPhoto, toBase64 } from './PersonAvatar'
import { useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { can as canArea, hasPower, useAccess } from '../../lib/access'
import { GrantsSection, SetMatrix } from './AccessDetails'
import { RateCorrection, type RateCorrectionEntry } from './RateCorrection'
import { DateField } from '../../components/DateField'

// The person record (handoff §7b): one panel for Add worker and for editing a
// row, differing only in the empty state and the save label. The server half
// is apps/api/src/routes/personnel.js; only fields the admin touched are sent.

export interface ClientApp {
  app: 'control' | 'crew'
  platform: 'android' | 'ios' | 'web' | 'unknown'
  version: string | null
  last_seen_at: string
}

export interface Person {
  id: string
  first_name: string
  last_name: string
  preferred_name: string | null
  email: string | null
  employee_id: string | null
  phone: string | null
  origin: string
  // Each payroll provider employee this person holds (the list's Connections column).
  provider_links?: { type: string; name: string; employee_id: string }[]
  // How the person's sign-in has signed in here, as Firebase names it: 'google.com', 'password', ...
  sign_in_methods?: string[]
  // How they sign in to Dataphyll Crew, named the same way: 'apple.com', 'google.com', ...
  crew_sign_ins?: string[]
  // The apps they use, on what, at which version, last used first (166).
  client_apps?: ClientApp[]
  archived: boolean
  exclude_from_wages: boolean
  hourly_rate: number | string | null
  rate_effective_from: string | null
  next_rate: number | string | null
  next_rate_from: string | null
  group_id: string | null
  group_name: string | null
  default_team_id: string | null
  default_team_name: string | null
  job_roles: string[] | null
  membership_id: string | null
  permission_set: string | null
  // The email their CONTROL sign-in names, and whether an account has claimed it.
  sign_in_email?: string | null
  has_signed_in?: boolean
  last_active: string | null
  merged_into_id?: string | null
  // When the current profile photo was set; null when there is none.
  photo_at?: string | null
}

const CREW_PROVIDERS: Record<string, string> = { 'apple.com': 'Apple', 'google.com': 'Google', 'facebook.com': 'Facebook', 'microsoft.com': 'Microsoft' }

// Who withheld this person's email: a Dataphyll Crew sign-in that gave none CONTROL can use (an Apple relay
// address, or an unverified one), so the person has no email. Null when there is one, or no Crew sign-in.
export function withheldBy(p: Pick<Person, 'email' | 'crew_sign_ins'> | null | undefined): string | null {
  if (!p || p.email || !p.crew_sign_ins?.length) return null
  return CREW_PROVIDERS[p.crew_sign_ins[0]] || p.crew_sign_ins[0]
}

interface RateVersion {
  id: string
  amount: number | string
  effective_from: string
  effective_to: string | null
}
interface MergeEntry {
  id: string
  action: 'merge' | 'unmerge'
  merged_at: string
  is_master: boolean
  picks_moved: number | null
  timesheets_moved: number | null
  tags_moved: number | null
  other_first_name: string | null
  other_last_name: string | null
  other_email: string | null
  other_employee_id: string | null
  other_created_at?: string | null
  merged_by_email?: string | null
  undone?: boolean
  undoes_event_id?: string | null
  dates_requeued?: string[] | null
}
// A payroll employee this person holds (108), and how it was connected: "an
// imported-by-match should be visible on the worker record" (h 1296).
interface ProviderLink {
  integration_id: string
  integration_name: string
  integration_type: string
  provider_employee_id: string
  matched_by: 'email' | 'name' | 'created'
  created_at: string
  via_merge: boolean
  provider_snapshot: { first_name?: string; last_name?: string; email?: string | null }
}

const linkHow = (l: ProviderLink) => {
  const s = l.provider_snapshot || {}
  if (l.matched_by === 'email') return `Connected by email${s.email ? ` (${s.email})` : ''}`
  if (l.matched_by === 'name') return `Connected by first and last name${s.first_name ? ` (${s.first_name} ${s.last_name})` : ''}`
  return `Added to Personnel from ${l.integration_name}`
}

interface Detail extends Person {
  // Left out, with pay_hidden, for anyone without pay_data (approved data
  // boundary, D1 point 7).
  rateHistory?: RateVersion[]
  // Identifies the history as loaded, sent back with a correction so a stale one is refused.
  rateToken?: string
  rateCorrections?: RateCorrectionEntry[]
  pay_hidden?: boolean
  mergeHistory: MergeEntry[]
  providerLinks?: ProviderLink[]
  // What the signed-in admin may do to sign-in here (the server decides).
  signInAccess?: { can_manage: boolean; can_manage_admin: boolean }
}
interface GroupOpt {
  id: string
  name: string
  is_default: boolean
  is_archived: boolean
}
interface TeamOpt {
  id: string
  name: string
  active: boolean
  is_induction: boolean
}

export const fullName = (p: { first_name?: string | null; last_name?: string | null; preferred_name?: string | null }) =>
  [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ')

export const initialsOf = (p: { first_name?: string | null; last_name?: string | null }) =>
  ((p.first_name || '').charAt(0) + (p.last_name || '').charAt(0)).toUpperCase() || '?'

const RATE_CODES = ['RATE_REQUIRED', 'RATE_INVALID', 'RATE_BEFORE_CURRENT', 'INVALID_EFFECTIVE_FROM', 'DAY_LOCKED']

// The permission sets (handoff §7b.1): one per system user, a job shape
// rather than a list of rights. SYSADMIN is shown so an admin can see who else
// can reach their data, and is never given from here.
export const PERMISSION_SETS: { key: string; text: string }[] = [
  { key: 'SYSADMIN', text: 'Dataphyll support. Everything, including what a client cannot reach.' },
  { key: 'ADMIN', text: 'Full access to everything this client owns.' },
  { key: 'OPERATIONS', text: 'Runs the field: people, teams, sites, picking, batches. No wages.' },
  { key: 'PAYROLL', text: 'Timesheets and wages through to export; reads the operational data behind the money.' },
  { key: 'REPORTING', text: 'Looks, never touches. Growers, owners, anyone who only needs numbers.' },
]

interface Access {
  membership_id: string | null
  permission_set: string | null
  sign_in_email: string | null
  has_signed_in: boolean
}

function Switch({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className={o.switch} onClick={onClick} />
  )
}

function parseRate(v: string): number | null {
  const n = Number(v.replace(/[$,\s]/g, ''))
  return v.trim() && Number.isFinite(n) && n > 0 && n < 100000 ? Math.round(n * 100) / 100 : null
}

interface Props {
  person: Person | null
  onClose: () => void
  onSaved: (message: string) => void
  onOpenGroups: () => void
  // The photo saves on its own, straight away; the list refreshes behind.
  onPhotoChanged?: () => void
  // Sign-in and the permission set save on their own too (§7b.1).
  onAccessChanged?: () => void
}

export function PersonRecordPanel({ person, onClose, onSaved, onOpenGroups, onPhotoChanged, onAccessChanged }: Props) {
  const { user } = useAuth()
  const isNew = !person
  const today = todayLocal()
  // D1 as approved: a Personnel reader (PAYROLL) is offered no change, pay
  // rates are for ADMIN, PAYROLL and SYSADMIN (the server leaves them out
  // for anyone else), and Stock & Dispatch capabilities and wider reporting
  // are given by ADMIN or SYSADMIN. The server refuses each one regardless.
  const mine = useAccess()
  // Dataphyll support's profiles are Dataphyll's (migration 138); the server
  // refuses a client's change to them too.
  const supportLocked = person?.permission_set === 'SYSADMIN' && mine?.role !== 'SYSADMIN'
  const mayChange = !supportLocked && canArea(mine, 'personnel', isNew ? 'add' : 'edit')
  const mayAdd = canArea(mine, 'personnel', 'add')
  const mayGrant = hasPower(mine, 'grant_access')
  const [detail, setDetail] = useState<Detail | null>(null)
  const [groups, setGroups] = useState<GroupOpt[] | null>(null)
  const [teams, setTeams] = useState<TeamOpt[] | null>(null)
  const [roleOptions, setRoleOptions] = useState<string[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)

  const startRate = person?.hourly_rate == null ? '' : Number(person.hourly_rate).toFixed(2)
  const start = {
    first: person?.first_name || '',
    last: person?.last_name || '',
    preferred: person?.preferred_name || '',
    employeeId: person?.employee_id || '',
    email: person?.email || '',
    phone: person?.phone || '',
    groupId: person?.group_id || '',
    teamId: person?.default_team_id || '',
    roles: person?.job_roles || [],
    exclude: !!person?.exclude_from_wages,
    archived: !!person?.archived,
  }
  const [first, setFirst] = useState(start.first)
  const [last, setLast] = useState(start.last)
  const [preferred, setPreferred] = useState(start.preferred)
  const [employeeId, setEmployeeId] = useState(start.employeeId)
  const [email, setEmail] = useState(start.email)
  const [phone, setPhone] = useState(start.phone)
  const [rate, setRate] = useState(startRate)
  const [rateFrom, setRateFrom] = useState(today)
  const [groupId, setGroupId] = useState(start.groupId)
  const [teamId, setTeamId] = useState(start.teamId)
  const [roles, setRoles] = useState<string[]>(start.roles)
  const [exclude, setExclude] = useState(start.exclude)
  const [archived, setArchived] = useState(start.archived)
  // A system user who starts working a timesheet (h 694-696, 719): someone who
  // is both is paid hourly and so needs a rate. Nothing is saved until Save.
  const [startsHourly, setStartsHourly] = useState(false)
  // The photo popover (handoff §6c, 752-754): the camera on the avatar opens
  // it; choosing or removing a photo saves at once, apart from the form.
  const [photoAt, setPhotoAt] = useState<string | null>(person?.photo_at || null)
  const [photoMenu, setPhotoMenu] = useState(false)
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  // CONTROL sign-in (§7b.1): what this person has now, the set an admin has
  // picked and not yet confirmed, and the Give / Remove asks.
  const [access, setAccess] = useState<Access>({
    membership_id: person?.membership_id || null,
    permission_set: person?.permission_set || null,
    sign_in_email: person?.sign_in_email || null,
    has_signed_in: !!person?.has_signed_in,
  })
  const [pickSet, setPickSet] = useState<string | null>(null)
  const [granting, setGranting] = useState(false)
  const [removeAsk, setRemoveAsk] = useState(false)
  const [accessBusy, setAccessBusy] = useState(false)
  const [accessError, setAccessError] = useState<string | null>(null)
  const [sent, setSent] = useState<{ from: string; days_back: number; sent_count: number } | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  // The email question: asked here first, or by the server when an unused
  // sign-in invitation would move with the email (C-2(a)); then it carries
  // the server's own words.
  const [confirm, setConfirm] = useState<null | 'email' | 'move'>(null)
  const [moveMsg, setMoveMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Which write `busy` is: the save, or undoing a merge (h 3984-3992).
  const [busyKind, setBusyKind] = useState<'save' | 'undo'>('save')
  const slow = useSlow(busy)
  // Undo from the merge history: the entry asking to be confirmed, and why
  // the server refused one.
  const [undoAsk, setUndoAsk] = useState<string | null>(null)
  const [undoError, setUndoError] = useState<{ id: string; msg: string } | null>(null)

  useEffect(() => {
    let live = true
    const fail = (e: any) => live && setLoadError(e?.message || 'Could not load the record')
    apiJson<GroupOpt[]>(user, '/api/groups').then((r) => live && setGroups(Array.isArray(r) ? r : [])).catch(fail)
    apiJson<TeamOpt[]>(user, '/api/teams').then((r) => live && setTeams(Array.isArray(r) ? r : [])).catch(fail)
    apiJson<{ roles: string[] }>(user, '/api/personnel/roles').then((r) => live && setRoleOptions(r?.roles || [])).catch(fail)
    if (person) apiJson<Detail>(user, `/api/personnel/${person.id}`).then((d) => live && setDetail(d)).catch(fail)
    return () => {
      live = false
    }
  }, [person?.id])

  // A new worker lands in the default group unless another is chosen (§7c).
  useEffect(() => {
    if (isNew && groups && !groupId) {
      const d = groups.find((x) => x.is_default)
      if (d) setGroupId(d.id)
    }
  }, [groups])

  // A new worker starts on the workspace's default rate (Personnel settings:
  // "Workers added without a minimum rate will get this rate"), to keep or
  // change before saving. Nothing typed is overwritten; without the setting,
  // or permission to read it, the field starts empty as before.
  useEffect(() => {
    if (!isNew || !hasPower(mine, 'pay_data')) return
    let live = true
    apiJson<{ default_min_rate: number | null }>(user, '/api/wages/settings')
      .then((s) => {
        const d = s?.default_min_rate
        if (live && d != null && Number(d) > 0) setRate((r) => (r.trim() ? r : Number(d).toFixed(2)))
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  const history = detail?.rateHistory || []
  const payShown = hasPower(mine, 'pay_data') && !detail?.pay_hidden
  // Only a system user can go unpaid; everyone created here is a field worker.
  const systemOnly = !!access.membership_id && !!person && person.hourly_rate == null && person.next_rate == null && (detail ? history.length === 0 : true)
  const paidHourly = !systemOnly || startsHourly
  const legacyNoRate = payShown && !isNew && !systemOnly && person!.hourly_rate == null && person!.next_rate == null && detail !== null && history.length === 0

  const rateValue = parseRate(rate)
  const rateChanged = paidHourly && rate.trim() !== startRate && rateValue !== (startRate ? Number(startRate) : null)
  const backdated = rateChanged && rateFrom < today

  // The backdate notice: how far back, and how many of this person's days in
  // that stretch already went to payroll. Counted by the server per person.
  useEffect(() => {
    setSent(null)
    if (isNew || !backdated || !/^\d{4}-\d{2}-\d{2}$/.test(rateFrom)) return
    let live = true
    apiJson<{ from: string; days_back: number; sent_count: number }>(user, `/api/personnel/${person!.id}/sent-since?from=${rateFrom}`)
      .then((r) => live && setSent(r))
      .catch(() => live && setSent(null))
    return () => {
      live = false
    }
  }, [backdated, rateFrom, person?.id])

  const emailChanged = !isNew && email.trim() !== start.email
  const sameRoles = [...roles].sort().join('|') === [...start.roles].sort().join('|')
  const dirty =
    first !== start.first ||
    last !== start.last ||
    preferred !== start.preferred ||
    employeeId !== start.employeeId ||
    email !== start.email ||
    phone !== start.phone ||
    rateChanged ||
    startsHourly ||
    (isNew && rateFrom !== today) ||
    groupId !== start.groupId ||
    teamId !== start.teamId ||
    !sameRoles ||
    exclude !== start.exclude ||
    archived !== start.archived

  // What is still needed before save: first name, last name, and a rate for
  // anyone paid by the hour.
  const missing: string[] = []
  if (!first.trim()) missing.push('first name')
  if (!last.trim()) missing.push('last name')
  if (payShown && paidHourly && rateValue === null) missing.push('hourly rate')
  const blocked = missing.length > 0

  // The panel, its photo popover and the email question are layers (h 425-458):
  // one Escape closes the topmost, and the panel asks first if it holds changes.
  const panelEl = useRef<HTMLDivElement>(null)
  const layer = useLayer(true, onClose, { dirty: dirty && mayChange && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  useLayer(photoMenu, () => setPhotoMenu(false))
  useLayer(confirm === 'email' || confirm === 'move', () => setConfirm(null))
  // The sign-in and merge questions are inline layers too: Escape is their Keep.
  useLayer(removeAsk, () => !accessBusy && setRemoveAsk(false))
  useLayer(!!pickSet && !granting, () => !accessBusy && setPickSet(null))
  useLayer(granting, () => {
    if (accessBusy) return
    setGranting(false)
    setPickSet(null)
  })
  useLayer(!!undoAsk, () => !busy && setUndoAsk(null))
  const requestClose = layer.requestClose
  // The scrim closes the photo popover before the panel, as Escape does.
  const onScrim = () => (photoMenu ? setPhotoMenu(false) : requestClose())
  const openGroups = () => layer.guard(onOpenGroups)

  // The pickers read their registers; the stored value is always kept in the
  // list so a save can never silently reassign someone.
  const groupOpts = (groups || []).filter((x) => !x.is_archived || x.id === start.groupId)
  if (start.groupId && groups && !groupOpts.some((x) => x.id === start.groupId)) {
    groupOpts.push({ id: start.groupId, name: person!.group_name || 'Current group', is_default: false, is_archived: true })
  }
  // An induction team is never offered, not even as the stored one.
  const teamOpts = (teams || []).filter((t) => !t.is_induction && (t.active || t.id === start.teamId))
  if (start.teamId && teams && !teams.some((t) => t.id === start.teamId)) {
    teamOpts.push({ id: start.teamId, name: person!.default_team_name || 'Current team', active: false, is_induction: false })
  }
  const roleList = [...roleOptions, ...start.roles.filter((r) => !roleOptions.includes(r))]

  const who = fullName({ first_name: first.trim() || person?.first_name, last_name: last.trim() || person?.last_name, preferred_name: preferred.trim() || null }) || 'this person'

  // An invitation nobody has used moves with the email (C-2(a)); a sign-in
  // someone has used never does. Both need the right to manage sign-in.
  const invitation = !!access.membership_id && !access.has_signed_in
  const save = async (confirmedEmail = false, moveSignIn = false) => {
    if (blocked || busy || (!isNew && !dirty)) return
    if (emailChanged && !confirmedEmail) {
      setConfirm(invitation && email.trim() ? 'move' : 'email')
      setMoveMsg(null)
      return
    }
    setConfirm(null)
    setBusyKind('save')
    setBusy(true)
    setSaveError(null)
    setErrors({})
    const body: Record<string, unknown> = {}
    const text = (k: string, v: string, was: string) => {
      if (isNew ? v.trim() : v.trim() !== was) body[k] = v.trim() || null
    }
    if (isNew || first.trim() !== start.first) body.firstName = first.trim()
    if (isNew || last.trim() !== start.last) body.lastName = last.trim()
    text('preferredName', preferred, start.preferred)
    text('employeeId', employeeId, start.employeeId)
    text('email', email, start.email)
    text('phone', phone, start.phone)
    if (payShown && paidHourly && (isNew || rateChanged)) {
      body.hourlyRate = rateValue
      body.hourlyRateEffectiveFrom = rateFrom
    }
    if (groupId && (isNew || groupId !== start.groupId)) body.groupId = groupId
    if (isNew ? teamId : teamId !== start.teamId) body.defaultTeamId = teamId || null
    if (isNew ? roles.length : !sameRoles) body.roles = roles
    if (isNew ? exclude : exclude !== start.exclude) body.excludeFromWages = exclude
    if (!isNew && archived !== start.archived) body.archived = archived
    if (moveSignIn) body.moveSignIn = true
    try {
      const saved = await apiJson<
        Person & {
          locked_unchanged?: string[]
          sign_in_removed?: string[]
          sign_in_moved?: { from: string; to: string }
          sign_in_unchanged?: { sign_in_email: string }
        }
      >(user, isNew ? '/api/personnel' : `/api/personnel/${person!.id}`, {
        method: isNew ? 'POST' : 'PUT',
        body,
      })
      const parts = [`${fullName(saved)} ${isNew ? 'created' : 'saved'}`]
      if (saved.locked_unchanged?.length) parts.push(`${plural(saved.locked_unchanged.length, 'locked day keeps', 'locked days keep')} their locked pay`)
      if (saved.sign_in_removed?.length) parts.push('CONTROL sign-in removed')
      if (saved.sign_in_moved) parts.push(`sign-in invitation moved to ${saved.sign_in_moved.to}`)
      if (saved.sign_in_unchanged) parts.push(`still signs in as ${saved.sign_in_unchanged.sign_in_email}`)
      onSaved(parts.join(' · '))
    } catch (e: any) {
      const code = e instanceof ApiError ? e.body?.code : null
      const msg = e?.message || 'The profile was not saved'
      // The server found an invitation to move that this panel did not know
      // of: ask again in its words, and send the confirmation with the save.
      if (code === 'SIGN_IN_WILL_MOVE' && !moveSignIn) {
        setMoveMsg(msg)
        setConfirm('move')
        setBusy(false)
        return
      }
      // EMAIL_TAKEN: someone else here has the address (one email, one
      // person), also when restoring; EMAIL_IN_USE: it is another's CONTROL
      // sign-in. Either way the fix is in the email field.
      const field =
        code === 'NAME_REQUIRED' || code === 'NAME_INVALID'
          ? 'name'
          : code === 'EMAIL_INVALID' || code === 'EMAIL_TAKEN' || code === 'EMAIL_IN_USE'
            ? 'email'
            : RATE_CODES.includes(code)
              ? 'rate'
              : code === 'GROUP_NOT_FOUND' || code === 'GROUP_ARCHIVED'
                ? 'group'
                : code === 'TEAM_NOT_FOUND' || code === 'TEAM_NOT_OFFERED'
                  ? 'team'
                  : code === 'ROLE_INVALID'
                    ? 'roles'
                    : null
      if (field) setErrors({ [field]: msg })
      else setSaveError(msg)
      setBusy(false)
    }
  }

  const setPhoto = async (file: File | null) => {
    if (photoBusy || !person) return
    setPhotoBusy(true)
    setPhotoError(null)
    try {
      let data: string | null = null
      if (file) {
        if (!/^image\/(jpeg|png|webp)$/.test(file.type)) throw new Error('That file is not a JPEG, PNG or WebP image.')
        const small = await shrinkPhoto(file)
        if (small.size > PHOTO_MAX) throw new Error('The photo is larger than 256 KB, even made smaller. Choose a smaller image.')
        data = await toBase64(small)
      }
      const r = await apiJson<{ photo_at: string | null }>(user, `/api/personnel/${person.id}/photo`, { method: 'PUT', body: { data } })
      setPhotoAt(r.photo_at)
      setPhotoMenu(false)
      onPhotoChanged?.()
    } catch (e: any) {
      setPhotoError(e?.message || 'The photo was not saved')
    } finally {
      setPhotoBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  // Give sign-in with a set, change the set, or remove sign-in (null). Saved
  // at once, apart from Save changes; the server checks who may (§7b.1).
  const setSignIn = async (set: string | null) => {
    if (accessBusy || !person) return
    setAccessBusy(true)
    setAccessError(null)
    try {
      const r = await apiJson<Access>(user, `/api/personnel/${person.id}/sign-in`, { method: 'PUT', body: { permissionSet: set } })
      setAccess(r)
      setPickSet(null)
      setGranting(false)
      setRemoveAsk(false)
      onAccessChanged?.()
    } catch (e: any) {
      setAccessError(e?.message || 'Sign-in was not changed')
    } finally {
      setAccessBusy(false)
    }
  }

  // Undo moves back only what the merge moved and is still where it put it;
  // work recorded or re-credited since stays where it is (handoff 817-818).
  const undoMerge = async (entry: MergeEntry) => {
    if (busy) return
    setBusyKind('undo')
    setBusy(true)
    setUndoError(null)
    try {
      const r = await apiJson<{ picks_moved: number; timesheets_moved: number; tags_moved: number; kept: number; queued: number }>(
        user,
        `/api/personnel/merges/${entry.id}/undo`,
        { body: {} }
      )
      const other = [entry.other_first_name, entry.other_last_name].filter(Boolean).join(' ') || 'The other profile'
      const parts = [`${other} unmerged`, `${plural(r.picks_moved, 'pick', 'picks')}, ${plural(r.timesheets_moved, 'timesheet', 'timesheets')}, ${plural(r.tags_moved, 'tag', 'tags')} moved back`]
      if (r.kept) parts.push(`${plural(r.kept, 'item', 'items')} changed since the merge kept where ${r.kept === 1 ? 'it is' : 'they are'}`)
      if (r.queued) parts.push(`${plural(r.queued, 'worker/day', 'worker/days')} queued for recalculation`)
      onSaved(parts.join(' · '))
    } catch (e: any) {
      setUndoError({ id: entry.id, msg: e?.message || 'The merge was not undone' })
      setUndoAsk(null)
      setBusy(false)
    }
  }

  const inputFor = (needed: boolean) => (needed ? `${o.input} ${o.inputBad}` : o.input)
  const fieldErr = (k: string) =>
    errors[k] ? (
      <span className={styles.fieldError} role="alert">
        {errors[k]}
      </span>
    ) : null

  // Who may change sign-in comes from the server with the record; until it
  // arrives nothing is offered.
  const can = detail?.signInAccess || { can_manage: false, can_manage_admin: false }
  const lockedSet = access.permission_set === 'SYSADMIN' || (access.permission_set === 'ADMIN' && !can.can_manage_admin)
  const setOff = (key: string) =>
    !can.can_manage || accessBusy || key === 'SYSADMIN' || (key === 'ADMIN' && !can.can_manage_admin) || (!!access.membership_id && lockedSet)
  const emailReady = !!start.email && !emailChanged
  const setPicker = (selected: string, onPick: (key: string) => void) => (
    <div className={styles.sets} role="radiogroup" aria-label="Permission set">
      {PERMISSION_SETS.map((p) => (
        <button
          key={p.key}
          type="button"
          role="radio"
          aria-checked={selected === p.key}
          disabled={setOff(p.key)}
          className={selected === p.key ? styles.setOn : styles.set}
          onClick={() => onPick(p.key)}
        >
          <span className={styles.setDot} aria-hidden="true" />
          <span className={styles.setBody}>
            <span className={styles.setKey}>
              {p.key}
              {p.key === 'SYSADMIN' && <span className={styles.setTag}>Dataphyll only</span>}
            </span>
            <span className={styles.setText}>{p.text}</span>
          </span>
        </button>
      ))}
    </div>
  )

  const title = isNew ? 'New worker' : fullName(person!)
  const meta = isNew
    ? 'Nothing is saved until you create the profile. A photo can be added once it exists.'
    : access.membership_id
      ? `System user${access.permission_set ? ' · ' + access.permission_set : ''}`
      : person!.merged_into_id
        ? 'Archived · merged into another profile'
        : archived
          ? 'Archived'
          : 'Field worker'

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && onScrim()}>
      <div ref={panelEl} tabIndex={-1} className={o.drawer} role="dialog" aria-label={isNew ? 'New worker' : title}>
        <div className={o.drawerHead}>
          {isNew ? (
            <span className={`${styles.avatar} ${styles.avatarNew}`} aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </span>
          ) : (
            <div className={styles.avatarWrap}>
              <PersonAvatar
                id={person!.id}
                photoAt={photoAt}
                initials={initialsOf(person!)}
                className={`${styles.avatar} ${archived ? styles.avatarNew : access.membership_id ? styles.avatarSystem : ''}`}
              />
              {!person!.merged_into_id && mayChange && (
                <button
                  type="button"
                  className={styles.camera}
                  aria-label={photoAt ? 'Change photo' : 'Add photo'}
                  aria-expanded={photoMenu}
                  onClick={() => {
                    setPhotoError(null)
                    setPhotoMenu((v) => !v)
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
                    <circle cx="12" cy="13" r="3.5" />
                  </svg>
                </button>
              )}
              {photoMenu && (
                <div className={styles.photoMenu} role="dialog" aria-label="Profile photo">
                  <div className={styles.photoHead}>
                    <span className={styles.photoTitle}>{photoAt ? 'Replace photo' : 'Add a photo'}</span>
                    <button type="button" className={styles.photoClose} aria-label="Close photo options" onClick={() => setPhotoMenu(false)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    aria-label="Photo file"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) setPhoto(f)
                    }}
                  />
                  <PendingButton className={styles.photoDrop} busy={photoBusy} pending="Saving…" onClick={() => fileRef.current?.click()}>
                    <span className={styles.photoDropIcon} aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 16V4M7 9l5-5 5 5" />
                        <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                      </svg>
                    </span>
                    <span className={styles.photoDropTitle}>{photoAt ? 'Choose another photo' : 'Choose a photo'}</span>
                    <span className={styles.photoDropNote}>JPEG, PNG or WebP. Saved as soon as it is chosen.</span>
                  </PendingButton>
                  {photoAt && (
                    <button type="button" className={styles.photoRemove} disabled={photoBusy} onClick={() => setPhoto(null)}>
                      Remove photo
                    </button>
                  )}
                  <span className={styles.photoNote}>{photoAt ? 'The photo shows in the list and on this record.' : 'No photo yet: the list shows initials.'}</span>
                  {photoError && (
                    <span className={styles.fieldError} role="alert">
                      {photoError} Nothing was changed.
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <div className={o.headText}>
            <h2 className={`${o.drawerTitle} ${styles.title}`}>{title}</h2>
            <p className={o.drawerSub}>{meta}</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={`${o.drawerBody} ${inertClass(busy, slow)}`}>
          {loadError && <div className={styles.reason}>{loadError}</div>}
          {legacyNoRate && !rateValue && (
            <div className={styles.reason}>
              {fullName(person!)} has no hourly rate. It is the wage floor, and a worker with no rate cannot be paid correctly, so this profile cannot be saved until one is set.
            </div>
          )}

          <div className={o.section}>
            <span className={o.label}>Name</span>
            <div className={o.grid2}>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="pr-first">
                  First name <span className={o.req}>required</span>
                </label>
                <input id="pr-first" className={inputFor(!first.trim())} value={first} onChange={(e) => setFirst(e.target.value)} />
                {!first.trim() && <span className={styles.needed}>Enter a first name.</span>}
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="pr-last">
                  Last name <span className={o.req}>required</span>
                </label>
                <input id="pr-last" className={inputFor(!last.trim())} value={last} onChange={(e) => setLast(e.target.value)} />
                {!last.trim() && <span className={styles.needed}>Enter a last name.</span>}
              </div>
            </div>
            {fieldErr('name')}
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="pr-pref">
                Preferred name <span className={styles.optional}>optional</span>
              </label>
              <input id="pr-pref" className={o.input} value={preferred} placeholder="Same as first name" onChange={(e) => setPreferred(e.target.value)} />
            </div>
          </div>

          <div className={o.section}>
            <span className={o.label}>Identity &amp; contact</span>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="pr-emp">
                Employee ID <span className={styles.optional}>optional</span>
              </label>
              <input id="pr-emp" className={o.input} value={employeeId} placeholder="Not set" onChange={(e) => setEmployeeId(e.target.value)} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="pr-email">
                Email <span className={styles.optional}>optional</span>
              </label>
              <input
                id="pr-email"
                type="email"
                className={emailChanged ? `${o.input} ${styles.caution}` : o.input}
                value={email}
                placeholder={withheldBy(person) ? `Withheld (${withheldBy(person)})` : 'name@example.com'}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailChanged &&
                (access.membership_id && access.has_signed_in ? (
                  <div className={o.warn}>
                    <InfoIcon />
                    <span>
                      {fullName(person!)} signs in to CONTROL as {access.sign_in_email}. A sign-in that has been used stays with that account, so changing the email here
                      does not change how they sign in.
                    </span>
                  </div>
                ) : invitation ? (
                  <div className={o.warn}>
                    <InfoIcon />
                    <span>
                      {fullName(person!)} was invited to sign in to CONTROL as {access.sign_in_email} and has not yet.{' '}
                      {can.can_manage
                        ? 'Saving moves the invitation to the new address, so only it can claim the sign-in.'
                        : 'Changing the email would move that invitation. Your permission set cannot manage CONTROL sign-in, so it cannot move the invitation; only an ADMIN can. Put the email back to save your other changes.'}
                    </span>
                  </div>
                ) : (
                  <div className={o.warn}>
                    <InfoIcon />
                    <span>
                      This is how {fullName(person!)} signs in. Sign-in is federated, so the email is their identity: change it and they lose access until they sign in
                      with the new address, and if they sign up again themselves you get a second profile to merge.
                    </span>
                  </div>
                ))}
              {fieldErr('email')}
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="pr-phone">
                Phone <span className={styles.optional}>optional</span>
              </label>
              <input id="pr-phone" className={o.input} value={phone} placeholder="Not set" onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>

          <div className={o.section}>
            <span className={o.label}>Pay &amp; placement</span>
            {!payShown ? (
              <div className={o.field}>
                <span className={o.fieldLabel}>Hourly rate</span>
                <div className={styles.notPaid}>Pay rates are shown to ADMIN, PAYROLL and SYSADMIN only.</div>
              </div>
            ) : paidHourly ? (
              <div className={o.section} role="group" aria-label="Hourly rate">
                {startsHourly && (
                  <div className={styles.next}>
                    {fullName(person!)} keeps their CONTROL sign-in and is paid hourly from the date below.{' '}
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => {
                        setStartsHourly(false)
                        setRate(startRate)
                        setRateFrom(today)
                      }}
                    >
                      Keep not paid hourly
                    </button>
                  </div>
                )}
                <div className={o.grid2}>
                  <div className={o.field}>
                    <label className={o.fieldLabel} htmlFor="pr-rate">
                      Hourly rate (floor) <span className={o.req}>required</span>
                    </label>
                    <input id="pr-rate" inputMode="decimal" className={`${inputFor(rateValue === null)} ${styles.rateInput}`} value={rate} placeholder="$ per hour" onChange={(e) => setRate(e.target.value)} />
                    {rateValue === null && <span className={styles.needed}>{rate.trim() ? 'Enter a rate per hour above $0.' : 'Set an hourly rate. It is the wage floor.'}</span>}
                  </div>
                  <div className={o.field}>
                    <label className={o.fieldLabel} htmlFor="pr-from">
                      Effective from
                    </label>
                    <DateField id="pr-from" className={o.input} value={rateFrom} onChange={(e) => setRateFrom(e.target.value || today)} />
                  </div>
                </div>
                <span className={o.fieldHint}>The worker's floor: piece-rate work can earn more, and Wages tops them up to this when it earns less. A new rate starts a new period. To fix a past rate, use Rate corrections below, which keeps the old rate on record.</span>
                {person?.next_rate != null && (
                  <span className={styles.next}>
                    Changes to {money(Number(person.next_rate))}/h on {dmy(person.next_rate_from)}. The current rate stays live until then.
                  </span>
                )}
                {backdated && sent && sent.sent_count > 0 && (
                  <div className={`${o.warn} ${styles.info}`} data-testid="backdate-notice">
                    <InfoIcon />
                    <span>
                      This rate reaches back {plural(sent.days_back, 'day', 'days')}, to {dmy(rateFrom)}. {plural(sent.sent_count, 'day', 'days')} of {fullName(person!)}'s in that
                      stretch {sent.sent_count === 1 ? 'was' : 'were'} already sent to payroll. Check the payroll system for what {sent.sent_count === 1 ? 'that day was' : 'those days were'} paid
                      at: the payroll system is the authority on what was paid, and CONTROL does not calculate a difference for {sent.sent_count === 1 ? 'it' : 'them'}.
                    </span>
                  </div>
                )}
                {fieldErr('rate')}

                {!isNew && (
                  <>
                    <div className={styles.subHead}>
                      <span className={styles.subTitle}>Rate history</span>
                      <span className={styles.subNote}>signed and locked: a new rate adds a version, it never overwrites</span>
                    </div>
                    {detail === null ? (
                      <div className={styles.empty}>Loading…</div>
                    ) : history.length === 0 ? (
                      <div className={styles.empty}>No rate recorded yet.</div>
                    ) : (
                      <div className={styles.versions}>
                        {history.map((v) => {
                          const live = !v.effective_to && v.effective_from <= today
                          return (
                            <div key={v.id} className={live ? styles.versionLive : styles.version} data-testid="rate-version">
                              <span className={styles.versionAmt}>{money(Number(v.amount))}</span>
                              <span className={styles.versionSpan}>
                                {v.effective_to ? `${dmy(v.effective_from)} – ${dmy(v.effective_to)}` : live ? `${dmy(v.effective_from)} – present` : `From ${dmy(v.effective_from)}`}
                              </span>
                              {live && <span className={styles.liveBadge}>Live</span>}
                            </div>
                          )
                        })}
                      </div>
                    )}
                    <RateCorrection
                      personId={person!.id}
                      name={fullName(person!)}
                      token={detail?.rateToken}
                      corrections={detail?.rateCorrections || []}
                      mayCorrect={mayChange && detail !== null && history.length > 0}
                      onChanged={() => {
                        apiJson<Detail>(user, `/api/personnel/${person!.id}`).then(setDetail).catch(() => {})
                        onAccessChanged?.()
                      }}
                    />
                  </>
                )}
              </div>
            ) : (
              <div className={o.field}>
                <span className={o.fieldLabel}>Hourly rate</span>
                <div className={styles.notPaid}>
                  Not paid hourly. A system user who never works a timesheet needs no rate.{' '}
                  {!archived && (
                    <button type="button" className={styles.linkBtn} onClick={() => setStartsHourly(true)}>
                      Starts working a timesheet? Add an hourly rate
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="pr-team">
                Default team <span className={styles.optional}>optional</span>
              </label>
              <select id="pr-team" className={o.input} value={teamId} disabled={!teams} onChange={(e) => setTeamId(e.target.value)}>
                <option value="">No default team</option>
                {teamOpts.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {!t.active ? ' (not active)' : ''}
                  </option>
                ))}
              </select>
              <span className={o.fieldHint}>Only used by the Preassign station and scheduled sign-ons. It does not restrict which team they can work in.</span>
              {fieldErr('team')}
            </div>

            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="pr-group">
                Group
              </label>
              <select id="pr-group" className={o.input} value={groupId} disabled={!groups} onChange={(e) => setGroupId(e.target.value)}>
                {!groups && <option value={groupId}>Loading…</option>}
                {groupOpts.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.is_archived ? ' (archived)' : ''}
                  </option>
                ))}
              </select>
              <span className={o.fieldHint}>
                Group you need not set up yet?{' '}
                <button type="button" className={styles.linkBtn} onClick={openGroups}>
                  Close this and open Groups.
                </button>
              </span>
              {fieldErr('group')}
            </div>
          </div>

          <div className={o.section} role="group" aria-label="Roles">
            <div>
              <span className={o.label}>
                Roles <span className={styles.optional}>optional</span>
              </span>
              <div className={`${o.fieldHint} ${styles.labelNote}`}>
                Labels for finding people ("show me my pickers"). They grant nothing: CONTROL access comes only from a permission set, and app access from the team's clock-on QR.
              </div>
            </div>
            <div className={styles.roles}>
              {roleList.map((r) => {
                const on = roles.includes(r)
                return (
                  <button key={r} type="button" aria-pressed={on} className={on ? styles.roleOn : styles.role} onClick={() => setRoles(on ? roles.filter((x) => x !== r) : [...roles, r])}>
                    {r}
                  </button>
                )
              })}
            </div>
            {fieldErr('roles')}
          </div>

          {!isNew &&
            !person!.merged_into_id &&
            (access.membership_id ? (
              <div className={`${o.section} ${styles.ruled}`} role="group" aria-label="Permissions">
                <div>
                  <span className={o.label}>Permissions</span>
                  <div className={`${o.fieldHint} ${styles.labelNote}`}>
                    The permission set is the only thing that opens a door in CONTROL; roles grant nothing. What each set grants is Dataphyll's to change, elsewhere, so nobody can
                    widen their own access from here.
                  </div>
                </div>
                {setPicker(pickSet || access.permission_set || '', (k) => {
                  setAccessError(null)
                  setRemoveAsk(false)
                  setPickSet(k === access.permission_set ? null : k)
                })}
                {/* What the set opens, from the definitions the server enforces (D1.1). */}
                <SetMatrix user={user} set={pickSet || access.permission_set || ''} />
                <span className={o.fieldHint}>
                  {access.has_signed_in
                    ? `Signs in with ${access.sign_in_email}.`
                    : `Has not signed in yet. They sign in with ${access.sign_in_email} through Google, Microsoft, Apple or Meta. CONTROL sends no email from here, so let them know, or invite them from System users instead.`}
                </span>
                {detail && !can.can_manage && <span className={o.fieldHint}>Only an ADMIN can change sign-in.</span>}
                {can.can_manage && lockedSet && (
                  <span className={o.fieldHint}>{access.permission_set === 'SYSADMIN' ? "SYSADMIN access is Dataphyll's to change." : 'Only an ADMIN can change or remove ADMIN.'}</span>
                )}
                {pickSet ? (
                  <div className={styles.accessAsk} role="group" aria-label="Confirm the permission set">
                    <span>
                      Change {who}'s permission set from {access.permission_set} to {pickSet}?
                    </span>
                    <button type="button" className={styles.smallGhost} disabled={accessBusy} onClick={() => setPickSet(null)}>
                      Keep {access.permission_set}
                    </button>
                    <PendingButton className={styles.smallPrimary} busy={accessBusy} pending="Saving…" onClick={() => setSignIn(pickSet)}>
                      {`Change to ${pickSet}`}
                    </PendingButton>
                  </div>
                ) : (
                  can.can_manage &&
                  !lockedSet &&
                  (removeAsk ? (
                    <div className={styles.accessAsk} role="group" aria-label="Confirm removing sign-in">
                      <span>Remove {who}'s CONTROL sign-in? They can no longer sign in to this workspace. Their profile and history stay.</span>
                      <button type="button" className={styles.smallGhost} disabled={accessBusy} onClick={() => setRemoveAsk(false)}>
                        Keep sign-in
                      </button>
                      <PendingButton className={styles.smallDanger} busy={accessBusy} pending="Removing…" onClick={() => setSignIn(null)}>
                        Remove sign-in
                      </PendingButton>
                    </div>
                  ) : (
                    <button type="button" className={styles.linkBtn} onClick={() => setRemoveAsk(true)}>
                      Remove CONTROL sign-in
                    </button>
                  ))
                )}
                {accessError && (
                  <span className={styles.fieldError} role="alert">
                    {accessError} Nothing was changed.
                  </span>
                )}
                {mayGrant && access.membership_id && (
                  <GrantsSection key={`${access.membership_id}:${access.permission_set}`} user={user} membershipId={access.membership_id} onChanged={onAccessChanged} />
                )}
              </div>
            ) : (
              <div className={`${o.section} ${styles.ruled}`} role="group" aria-label="CONTROL sign-in">
                <div>
                  <span className={o.label}>CONTROL sign-in</span>
                  <div className={`${o.fieldHint} ${styles.labelNote}`}>None. Field workers sign into the Crew App with their team's clock-on QR and need no CONTROL sign-in.</div>
                </div>
                {can.can_manage &&
                  (start.archived ? (
                    <span className={o.fieldHint}>Archived: bring them back before giving them sign-in.</span>
                  ) : granting ? (
                    <>
                      {setPicker(pickSet || 'REPORTING', (k) => setPickSet(k))}
                      <span className={o.fieldHint}>New system users start on REPORTING, the least dangerous set.</span>
                      <SetMatrix user={user} set={pickSet || 'REPORTING'} />
                      <div className={styles.accessAsk} role="group" aria-label="Confirm giving sign-in">
                        <span>
                          {who} will sign in with {start.email}. CONTROL sends no email from here, so let them know, or invite them from System users instead.
                        </span>
                        <button
                          type="button"
                          className={styles.smallGhost}
                          disabled={accessBusy}
                          onClick={() => {
                            setGranting(false)
                            setPickSet(null)
                          }}
                        >
                          Cancel
                        </button>
                        <PendingButton className={styles.smallPrimary} busy={accessBusy} pending="Saving…" onClick={() => setSignIn(pickSet || 'REPORTING')}>
                          {`Give sign-in as ${pickSet || 'REPORTING'}`}
                        </PendingButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={styles.linkBtn}
                        disabled={!emailReady}
                        onClick={() => {
                          setAccessError(null)
                          setPickSet(null)
                          setGranting(true)
                        }}
                      >
                        Give CONTROL sign-in
                      </button>
                      {!emailReady && (
                        <span className={o.fieldHint}>
                          {emailChanged ? 'Save the email change first: sign-in is given to the saved email.' : 'Add and save an email first: it is the account they sign in with.'}
                        </span>
                      )}
                    </>
                  ))}
                {accessError && (
                  <span className={styles.fieldError} role="alert">
                    {accessError} Nothing was changed.
                  </span>
                )}
              </div>
            ))}

          <div className={styles.toggles}>
            <div className={o.toggleRow}>
              <div className={o.toggleText}>
                <div className={o.toggleTitle}>Exclude from wages</div>
                <div className={o.toggleSub}>
                  {exclude
                    ? 'Excluded. Their timesheets stay recorded, but Wages leaves them out of pay and payroll exports. Locked days keep their locked pay; to change one, unlock that day on Timesheets first.'
                    : 'Switch on to leave them out of pay and payroll exports. Their timesheets stay recorded. Locked days keep their locked pay; to change one, unlock that day on Timesheets first.'}
                </div>
              </div>
              <Switch on={exclude} label="Exclude from wages" onClick={() => setExclude(!exclude)} />
            </div>

            {!isNew && (
              <div className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>Archived</div>
                  <div className={o.toggleSub}>
                    {archived ? 'Archived. Their history stays intact. Switch off to bring them back.' : 'Moves them to the Archived list. Nothing is deleted, so their history stays intact.'}
                  </div>
                </div>
                <Switch on={archived} label="Archived" onClick={() => setArchived(!archived)} />
              </div>
            )}
            {!isNew && archived && !start.archived && access.membership_id && (
              <div className={o.warn}>
                <InfoIcon />
                <span>
                  {can.can_manage
                    ? `Archiving removes ${who}'s CONTROL sign-in here. Bringing them back later does not give it back; give it again under Permissions if they need it.`
                    : `${who} signs in to CONTROL, and archiving removes that sign-in, so only an ADMIN can archive them.`}
                </span>
              </div>
            )}
          </div>

          {!isNew && detail?.providerLinks && detail.providerLinks.length > 0 && (
            <div className={o.section}>
              <span className={o.label}>Imported from payroll</span>
              {detail.providerLinks.map((l) => (
                <div key={`${l.integration_id}-${l.provider_employee_id}`} className={styles.merge} data-testid="provider-link">
                  <span className={styles.mergeName}>
                    {l.integration_name} employee {l.provider_employee_id}
                  </span>
                  <br />
                  {linkHow(l)} on {dmy(l.created_at)}
                  {l.via_merge ? ' · held by a profile since merged into this one' : ''}
                </div>
              ))}
            </div>
          )}

          {!isNew && (
            <div className={o.section}>
              <span className={o.label}>Merge history</span>
              {detail === null ? (
                <div className={styles.empty}>Loading…</div>
              ) : detail.mergeHistory.length === 0 ? (
                <div className={styles.empty}>Not merged with any other profile.</div>
              ) : (
                detail.mergeHistory.map((m) => (
                  <div key={m.id} className={styles.merge} data-testid="merge-entry">
                    <span className={styles.mergeName}>{[m.other_first_name, m.other_last_name].filter(Boolean).join(' ') || 'Another profile'}</span>
                    {m.other_email ? ` · ${m.other_email}` : ''}
                    {m.other_employee_id ? ` · ${m.other_employee_id}` : ''}
                    {m.is_master && m.action === 'merge' && m.other_created_at ? ` · signed up ${dmy(m.other_created_at)}` : ''}
                    <br />
                    {m.action === 'unmerge' ? 'Unmerged' : m.is_master ? 'Merged into this profile' : 'This profile was merged into it'} on {dmy(m.merged_at)}
                    {m.merged_by_email ? ` by ${m.merged_by_email}` : ''}
                    {m.action === 'merge' &&
                      ` · ${plural(m.picks_moved || 0, 'pick', 'picks')}, ${plural(m.timesheets_moved || 0, 'timesheet', 'timesheets')}, ${plural(m.tags_moved || 0, 'tag', 'tags')} moved`}
                    {m.action === 'merge' && m.undone && <span className={styles.mergeUndone}> · Undone</span>}
                    {m.action === 'merge' && m.is_master && !m.undone && (
                      <div className={styles.mergeActions}>
                        {undoAsk === m.id ? (
                          <>
                            <span>Undo this merge? Work recorded since stays with whoever it was scanned against.</span>
                            <button type="button" className={styles.smallGhost} disabled={busy} onClick={() => setUndoAsk(null)}>
                              Keep merged
                            </button>
                            <PendingButton className={styles.smallDanger} busy={busy && busyKind === 'undo'} pending="Undoing…" disabled={busy} onClick={() => undoMerge(m)}>
                              Undo merge
                            </PendingButton>
                          </>
                        ) : (
                          mayAdd && (
                            <button type="button" className={styles.smallGhost} disabled={busy} onClick={() => setUndoAsk(m.id)}>
                              Undo
                            </button>
                          )
                        )}
                      </div>
                    )}
                    {undoError?.id === m.id && (
                      <div className={styles.reason} role="alert">
                        {undoError.msg}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className={`${o.drawerFoot} ${styles.foot}`}>
          {saveError && (
            <div className={o.danger} role="alert">
              <span>{saveError}</span>
            </div>
          )}
          {confirm === 'move' && !can.can_manage ? (
            // Moving an unclaimed invitation needs the right to manage sign-in
            // (the server's signInAccess). Without it nothing is offered to
            // confirm; the server refuses the move regardless.
            <div className={`${o.warn} ${styles.ask}`} role="alert">
              <span>
                {`Changing ${who}'s email would move their unclaimed CONTROL sign-in invitation from ${access.sign_in_email || 'its current address'}. Your permission set cannot manage CONTROL sign-in, so only an ADMIN can move it. Put the email back to save your other changes.`}
              </span>
              <button type="button" className={styles.smallGhost} onClick={() => setConfirm(null)}>
                Keep editing
              </button>
            </div>
          ) : confirm === 'move' ? (
            <div className={`${o.warn} ${styles.ask}`} role="alert">
              <span>
                {moveMsg ||
                  `Move ${who}'s CONTROL sign-in invitation from ${access.sign_in_email} to ${email.trim()}? Only ${email.trim()} can claim it after this.`}
              </span>
              <button type="button" className={styles.smallGhost} onClick={() => setConfirm(null)}>
                Keep editing
              </button>
              <button type="button" className={styles.smallDanger} onClick={() => save(true, true)}>
                Move sign-in and save
              </button>
            </div>
          ) : confirm === 'email' ? (
            <div className={`${o.warn} ${styles.ask}`} role="alert">
              <span>
                {access.membership_id && access.has_signed_in
                  ? `Change ${who}'s email to ${email.trim() || 'nothing'}? They keep signing in as ${access.sign_in_email}.`
                  : `Change ${who}'s sign-in email to ${email.trim() || 'nothing'}? They cannot sign in until they use the new address.`}
              </span>
              <button type="button" className={styles.smallGhost} onClick={() => setConfirm(null)}>
                Keep editing
              </button>
              <button type="button" className={styles.smallDanger} onClick={() => save(true)}>
                Change email
              </button>
            </div>
          ) : (
            <>
              <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
                {mayChange ? 'Cancel' : 'Done'}
              </button>
              {!mayChange && (
                <span className={`${o.footNote} ${styles.footMuted}`}>
                  {supportLocked ? 'Dataphyll support. Only Dataphyll can change this profile.' : 'Your permission set can view personnel, not change them.'}
                </span>
              )}
              {mayChange && blocked && <span className={o.footNote}>{plural(missing.length, 'field', 'fields')} still needed</span>}
              {mayChange && (
                <PendingButton
                  className={o.primary}
                  busy={busy && busyKind === 'save'}
                  pending={isNew ? 'Creating…' : 'Saving…'}
                  disabled={blocked || busy || (!isNew && !dirty)}
                  title={blocked ? `Still needed: ${missing.join(', ')}` : !isNew && !dirty ? 'Nothing has changed yet' : undefined}
                  onClick={() => save()}
                >
                  {isNew ? 'Create profile' : 'Save changes'}
                </PendingButton>
              )}
            </>
          )}
        </div>
      </div>
      {layer.confirm}
    </div>
  )
}

function InfoIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  )
}
